<script setup>
import {computed, inject, onBeforeMount, provide, ref} from 'vue';
import {useForm} from "@inertiajs/vue3";
import Swal from 'sweetalert2';
import moment from "moment";
import WizardLayout from '@/Layouts/WizardLayout.vue';
import FormButton from '@/components/Shared/Form/Button.vue';
import UpdateBillingAddressModal from '@/components/Dashboard/UpdateBillingAddressModal.vue';

onBeforeMount(() => {
  provide('currentStep', 5);
});

const props = defineProps({
  integration: {
    type: Object,
    required: true,
  },
  flows: {
    type: Array,
    required: true,
  },
  user: Object
});

const trans = inject('trans')
const transChoice = inject('transChoice')

const isUpdateBillingAddressModalOpen = ref(false)
const hasValidBillingAddress = ref(props.user.has_valid_billing_address)

const eligibleTrialUntil = props.integration.eligible_trial_until
const eligibleTrialDays = props.integration.eligible_trial_days
const isEligibleForTrial = eligibleTrialUntil != null

const registrationDateFormatted = moment().format('D MMM YYYY')
const invoiceDateFormatted = isEligibleForTrial ? moment(eligibleTrialUntil).format('D MMM YYYY') : registrationDateFormatted

const totalFlowFees = computed(() => props.flows.reduce((acc, flow) => acc + flow.flow_price, 0))

const form = useForm({
  integration: props.integration.id,
  flows: []
})

const submit = async () => {
  let hasMandate = props.user.valid_mollie_mandate
  let isBillable = props.user.is_billable

  if (isBillable && !isEligibleForTrial) {
    if (!hasValidBillingAddress.value) {
      isUpdateBillingAddressModalOpen.value = true

      return
    }

    let paymentText = hasMandate
      ? trans('dashboard.integrations.wizard.fifth_step.require_subscription_via_mandate')
      : trans('dashboard.integrations.wizard.fifth_step.require_subscription_via_checkout')

    let result = await Swal.fire({
      text: paymentText,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: trans('common.confirm'),
      cancelButtonText: trans('common.cancel'),
    })

    if (!result.isConfirmed) {
      return
    }
  }

  form.post(route('wizard.step-5.store'))
}
</script>

<script>
import DashboardLayout from '@/Layouts/DashboardLayout.vue';

export default {
  layout: DashboardLayout,
};
</script>

<template>
  <wizard-layout>
    <template #default>
      <div class="wizard_grid">
        <h3 class="color_theme h4 mb-3">
          {{ trans('dashboard.integrations.wizard.step_x', {step: 5}) }} -
          <span class="color_normal">{{ trans('dashboard.integrations.wizard.fifth_step.title') }}</span>
        </h3>

        <div>
          <div>
            <table aria-describedby="list of flows" class="table table-hover table-striped">
              <thead>
              <tr class="thead">
                <th scope="col">#</th>
                <th scope="col">{{ trans('dashboard.integrations.wizard.fifth_step.item') }}</th>
                <th scope="col">{{ trans('dashboard.integrations.wizard.fifth_step.registration_date') }}</th>
                <th scope="col">{{ trans('dashboard.integrations.wizard.fifth_step.invoice_date') }}</th>
                <th scope="col">{{ trans('dashboard.integrations.wizard.fifth_step.start_flow_now') }}</th>
                <th scope="col">{{ trans('dashboard.integrations.wizard.fifth_step.price') }}</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(flow, index) in flows" :key="`flow-${flow.id}`">
                <td>{{ index + 1 }}</td>
                <td>{{ flow.first_flow.group.translated_name }}</td>
                <td>{{ registrationDateFormatted }}</td>
                <td>{{ invoiceDateFormatted }}</td>
                <td>
                  <input
                    :id="`check-flow-${flow.id}`"
                    v-model="form.flows"
                    :value="flow.id"
                    class="form_knob_check"
                    type="checkbox"
                  />
                  <label
                    :for="`check-flow-${flow.id}`"
                    class="form_knob flex text_normal my-0 w-90 mx-auto bg-transparent orphan"
                  >
                  </label>
                </td>
                <td class="w-90">€ {{ flow.flow_price.toFixed(2) }}</td>
              </tr>
              <tr>
                <td colspan="5"></td>
                <td>{{ trans('common.total') }}</td>
              </tr>
              <tr>
                <td colspan="5"></td>
                <td>€ {{ totalFlowFees.toFixed(2) }}</td>
              </tr>
              </tbody>
            </table>
            <p v-if="isEligibleForTrial" class="p-4 bg-20 mt-8">
              {{ transChoice('dashboard.integrations.wizard.fifth_step.first_x_days_free', eligibleTrialDays, {amount: eligibleTrialDays}) }}
              <br>
              {{ trans('dashboard.integrations.wizard.fifth_step.free_trial_ends_at_x', {date: moment(eligibleTrialUntil).format('Do of MMMM')}) }}
            </p>
          </div>
        </div>
        <update-billing-address-modal v-if="isUpdateBillingAddressModalOpen"
                                      @valid="() => hasValidBillingAddress = true"
                                      @close="() => isUpdateBillingAddressModalOpen = false"
                                      :user="props.user"/>
      </div>
    </template>
    <template #actions>
      <div class="grid-2 mt-2 relative z-10">
        <inertia-link
          class="button button_prev"
          :href="route('wizard.step-4.index', { integration: integration.id })"
          v-text="trans('pagination.previous')"
        />
        <form-button @click="submit" :value="trans('common.submit')" :processing="form.processing" @submit="submit"/>
      </div>
    </template>
  </wizard-layout>
</template>
